<template>
  <form class="user-form" @submit.prevent="handleSubmit">
    <div v-if="errorMessage" class="alert alert-danger py-2">
      {{ errorMessage }}
    </div>

    <div class="user-form-steps mb-3">
      <button
        type="button"
        class="user-form-step"
        :class="{ active: currentStep === 1, done: currentStep > 1 }"
        @click="currentStep = 1"
      >
        <span>1</span>
        Identité et site
      </button>
      <button
        type="button"
        class="user-form-step"
        :class="{ active: currentStep === 2 }"
        @click="goToPermissionsStep"
      >
        <span>2</span>
        {{ permissionsStepLabel }}
      </button>
    </div>

    <section v-if="currentStep === 1" class="row g-3">
      <div class="col-md-6">
        <label class="form-label fw-semibold">Nom complet</label>
        <PrimevueInputText
          v-model="form.name"
          class="w-100"
          placeholder="Nom complet de l'utilisateur"
          required
        />
      </div>

      <div v-if="!isEditMode" class="col-md-6">
        <label class="form-label fw-semibold">Nom d'utilisateur</label>
        <PrimevueInputText
          v-model="form.username"
          class="w-100"
          placeholder="Identifiant de connexion"
          required
        />
      </div>

      <div v-else class="col-md-6">
        <label class="form-label fw-semibold">Nom d'utilisateur</label>
        <div class="form-control user-form-readonly">{{ form.username || '-' }}</div>
        <small class="text-muted">
          Cet identifiant n’est plus modifiable afin de préserver les historiques liés au compte.
        </small>
      </div>

      <div class="col-md-6">
        <label class="form-label fw-semibold">Email</label>
        <PrimevueInputText
          v-model="form.email"
          class="w-100"
          type="email"
          placeholder="Email optionnel"
        />
      </div>

      <div class="col-md-6">
        <label class="form-label fw-semibold">Rôle</label>
        <PrimevueDropdown
          v-model="form.role"
          :options="roleOptions"
          optionLabel="label"
          optionValue="value"
          placeholder="Sélectionnez un rôle"
          class="w-100"
          @change="handleRoleChange"
        />
      </div>

      <div class="col-md-8">
        <label class="form-label fw-semibold">
          Site
          <span v-if="isSiteRequired" class="text-danger">*</span>
        </label>
        <PrimevueDropdown
          v-model="form.shop_id"
          :options="siteOptions"
          optionLabel="label"
          optionValue="value"
          placeholder="Sélectionnez un site"
          class="w-100"
          :showClear="!isSiteRequired"
          :disabled="siteOptions.length <= 1 && isSiteRequired"
        />
        <small class="text-muted">
          <template v-if="form.role === 'utilisateur'">
            Videz la sélection pour autoriser l’accès à toutes les boutiques actives du gérant.
          </template>
          <template v-else>
            Ce site reste le site principal du compte. Les affectations temporaires se gèrent depuis la fiche utilisateur.
          </template>
        </small>
      </div>

      <div class="col-md-4 d-flex align-items-center pt-md-4">
        <PrimevueCheckbox
          v-model="form.is_active"
          binary
          inputId="isActive"
          :disabled="isDemoUserActivationLocked"
        />
        <label for="isActive" class="form-check-label ms-2">
          Compte actif
        </label>
        <small v-if="isDemoUserActivationLocked" class="text-danger ms-2">
          Impossible pendant la demo
        </small>
      </div>
    </section>

    <section v-else>
      <div class="d-flex flex-wrap justify-content-between align-items-start gap-2 mb-3">
        <div>
          <h6 class="mb-1">Droits d’accès</h6>
          <p class="text-muted small mb-0">
            Cochez les modules et actions autorisés pour ce compte.
          </p>
        </div>
        <button
          v-if="canEditPermissions"
          type="button"
          class="btn btn-outline-secondary btn-sm btn-icon-label"
          @click="applyRoleDefaults"
        >
          <i class="fa fa-check-double"></i>
          <span class="btn-label ms-1">Appliquer le preset du rôle</span>
        </button>
      </div>

      <div v-if="!canEditPermissions" class="alert alert-info py-2">
        Les droits des rôles gérant, caissier et support sont gérés automatiquement par le rôle. Ils ne peuvent pas être personnalisés ici.
      </div>

      <div v-else-if="isCatalogLoading" class="alert alert-info py-2">
        Chargement des permissions...
      </div>

      <div v-else class="permission-grid">
        <div
          v-for="module in permissionModules"
          :key="module.key"
          class="permission-module"
        >
          <div class="d-flex justify-content-between align-items-center mb-2">
            <strong>{{ module.label }}</strong>
            <button
              type="button"
              class="btn btn-link btn-sm p-0"
              @click="toggleModule(module)"
            >
              {{ isModuleFullySelected(module) ? 'Tout retirer' : 'Tout cocher' }}
            </button>
          </div>

          <div class="permission-actions">
            <label
              v-for="action in module.actions"
              :key="action.key"
              class="permission-action"
            >
              <PrimevueCheckbox
                :modelValue="isPermissionSelected(action.key)"
                binary
                @update:modelValue="setPermission(action.key, $event)"
              />
              <span>{{ action.label }}</span>
            </label>
          </div>
        </div>
      </div>
    </section>

    <div class="d-flex justify-content-between mt-4 gap-2">
      <button
        v-if="currentStep === 2"
        type="button"
        class="btn btn-outline-secondary"
        :disabled="isSubmitting"
        @click="currentStep = 1"
      >
        <i class="fa fa-arrow-left me-1" aria-hidden="true"></i>
        Précédent
      </button>
      <span v-else></span>

      <div class="d-flex gap-2">
        <button
          type="button"
          class="btn btn-outline-secondary"
          @click="$emit('cancel')"
          :disabled="isSubmitting"
        >
        <i class="fa fa-xmark me-1" aria-hidden="true"></i>
        Annuler
        </button>

        <button
          v-if="currentStep === 1"
          type="button"
          class="btn btn-primary"
          @click="goToPermissionsStep"
        >
        <i class="fa fa-arrow-right me-1" aria-hidden="true"></i>
        Suivant
        </button>

        <AppLoadingButton
          v-else
          type="submit"
          class="btn btn-primary"
          :loading="isSubmitting"
          loading-label="Enregistrement..."
        >
        <i class="fa fa-save me-1" aria-hidden="true"></i>
        {{ isEditMode ? 'Mettre à jour' : 'Créer' }}
        </AppLoadingButton>
      </div>
    </div>
  </form>
</template>

<script>
import { defineComponent } from 'vue';
import { isDemoUser } from '@/support/permissions';

const DEFAULT_ROLE_OPTIONS = [
  { label: 'Caissier', value: 'caissier' },
  { label: 'Utilisateur', value: 'utilisateur' },
];

const ROLE_OPTION_ORDER = ['gerant', 'caissier', 'utilisateur', 'support', 'superadmin'];

const buildRoleOptions = (roles = null) => {
  if (!roles || typeof roles !== 'object') {
    return [...DEFAULT_ROLE_OPTIONS];
  }

  return ROLE_OPTION_ORDER
    .filter((role) => Object.prototype.hasOwnProperty.call(roles, role))
    .map((role) => ({
      label: roles[role],
      value: role,
    }));
};

export default defineComponent({
  name: 'UserForm',

  props: {
    mode: {
      type: String,
      required: true,
    },
    user: {
      type: Object,
      default: null,
    },
    allSitesMode: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      currentStep: 1,
      form: this.emptyForm(),
      roleOptions: DEFAULT_ROLE_OPTIONS,
      permissionCatalog: null,
      siteOptions: [],
      isCatalogLoading: false,
      isSubmitting: false,
      errorMessage: '',
    };
  },

  computed: {
    isEditMode() {
      return this.mode === 'edit';
    },
    isSiteRequired() {
      return this.form.role === 'caissier';
    },
    canEditPermissions() {
      return this.form.role === 'utilisateur';
    },
    permissionsStepLabel() {
      return this.canEditPermissions ? 'Modules et permissions' : 'Permissions du rôle';
    },
    currentUser() {
      return this.$store?.getters?.['auth/currentUser'] || null;
    },
    isDemoUserActivationLocked() {
      return this.isEditMode
        && isDemoUser(this.currentUser)
        && Number(this.form.id || 0) !== Number(this.currentUser?.id || 0);
    },
    permissionModules() {
      const modules = this.permissionCatalog?.modules || {};

      const assignable = this.permissionCatalog?.assignable_permissions;
      return Object.entries(modules).map(([key, module]) => ({
        key,
        label: module.label,
        actions: Object.entries(module.actions || {})
          .filter(([actionKey]) => !Array.isArray(assignable) || assignable.includes(`${key}.${actionKey}`))
          .map(([actionKey, label]) => ({
          key: `${key}.${actionKey}`,
          label,
        })),
      }));
    },
  },

  watch: {
    user: {
      immediate: true,
      handler(newVal) {
        if (newVal) {
          this.form = {
            id: newVal.id,
            name: newVal.name || '',
            username: newVal.username || '',
            email: newVal.email || '',
            role: newVal.role || 'utilisateur',
            shop_id: newVal.shop_id || null,
            is_active: newVal.is_active !== false,
            permissions: Array.isArray(newVal.permissions) ? [...newVal.permissions] : [],
          };
        } else {
          this.form = this.emptyForm();
          if (this.permissionCatalog) {
            this.applyRoleDefaults();
          }
        }
        this.currentStep = 1;
      },
    },
  },

  mounted() {
    this.loadCatalog();
    this.loadSites();
  },

  methods: {
    emptyForm() {
      const currentUser = this.$store?.getters?.['auth/currentUser'];
      const currentShopId = currentUser?.shop_id || this.$store?.getters?.['shop/currentShopId'] || null;

      return {
        id: null,
        name: '',
        username: '',
        email: '',
        role: 'utilisateur',
        shop_id: currentShopId ? Number(currentShopId) : null,
        is_active: true,
        permissions: [],
      };
    },

    async loadCatalog() {
      this.isCatalogLoading = true;
      try {
        const response = await this.$api.get('/users/permission-catalog');
        this.permissionCatalog = response.data.data;
        this.roleOptions = buildRoleOptions(this.permissionCatalog?.roles);

        if (!this.isEditMode && this.form.permissions.length === 0) {
          this.applyRoleDefaults();
        }
      } catch (error) {
        this.errorMessage = 'Impossible de charger le catalogue des permissions.';
      } finally {
        this.isCatalogLoading = false;
      }
    },

    async loadSites() {
      const currentUser = this.$store?.getters?.['auth/currentUser'];
      const currentShop = this.$store?.getters?.['shop/shopInfo'];

      try {
        const response = await this.$api.get('/shops', { params: { limit: 500 } });
        const raw = response.data?.data || response.data || [];
        const shops = Array.isArray(raw.data) ? raw.data : raw;
        this.siteOptions = shops.map((shop) => ({
          label: shop.name || shop.code || `Site #${shop.id}`,
          value: Number(shop.id),
        }));
      } catch (error) {
        if (currentUser?.shop_id || currentShop?.id) {
          this.siteOptions = [{
            label: currentShop?.name || currentUser?.shop?.name || `Site #${currentUser?.shop_id || currentShop?.id}`,
            value: Number(currentUser?.shop_id || currentShop?.id),
          }];
        }
      }

      if (!this.form.shop_id && currentUser?.shop_id) {
        this.form.shop_id = Number(currentUser.shop_id);
      }
    },

    handleRoleChange() {
      this.applyRoleDefaults();

      if (this.form.role === 'support') {
        this.form.shop_id = null;
        return;
      }

      if (!this.isSiteRequired && this.form.role === 'gerant') {
        return;
      }

      if (this.isSiteRequired && !this.form.shop_id && this.siteOptions.length === 1) {
        this.form.shop_id = this.siteOptions[0].value;
      }
    },

    applyRoleDefaults() {
      const defaults = this.permissionCatalog?.role_defaults?.[this.form.role] || [];
      const assignable = this.permissionCatalog?.assignable_permissions;
      this.form.permissions = defaults.filter((permission) => !Array.isArray(assignable) || assignable.includes(permission));
    },

    validateIdentityStep() {
      if (!this.form.name || !this.form.role || (!this.isEditMode && !this.form.username)) {
        this.errorMessage = this.isEditMode
          ? 'Renseignez le nom et le rôle.'
          : 'Renseignez le nom, l’identifiant et le rôle.';
        return false;
      }

      if (this.isSiteRequired && !this.form.shop_id) {
        this.errorMessage = 'Sélectionnez le site de cet utilisateur.';
        return false;
      }

      this.errorMessage = '';
      return true;
    },

    goToPermissionsStep() {
      if (!this.validateIdentityStep()) return;
      this.currentStep = 2;
    },

    isPermissionSelected(key) {
      return this.form.permissions.includes(key);
    },

    setPermission(key, checked) {
      if (checked && !this.form.permissions.includes(key)) {
        this.form.permissions.push(key);
      }

      if (!checked) {
        this.form.permissions = this.form.permissions.filter((permission) => permission !== key);
      }
    },

    isModuleFullySelected(module) {
      return module.actions.every((action) => this.isPermissionSelected(action.key));
    },

    toggleModule(module) {
      const shouldSelect = !this.isModuleFullySelected(module);

      module.actions.forEach((action) => {
        this.setPermission(action.key, shouldSelect);
      });
    },

    async handleSubmit() {
      if (this.isSubmitting) return;

      if (!this.validateIdentityStep()) {
        this.currentStep = 1;
        return;
      }

      this.errorMessage = '';
      this.isSubmitting = true;

      try {
        const payload = {
          name: this.form.name,
          email: this.form.email || null,
          role: this.form.role,
          shop_id: this.form.shop_id || null,
          is_active: this.isDemoUserActivationLocked ? this.user?.is_active !== false : this.form.is_active,
        };

        if (!this.isEditMode) {
          payload.username = this.form.username;
        }

        if (this.canEditPermissions) {
          payload.permissions = this.form.permissions;
        }

        let response;
        if (this.isEditMode && this.form.id) {
          response = this.allSitesMode
            ? await this.$api.put(`/users/${this.form.id}`, payload, { params: { all_sites: 1 } })
            : await this.$api.put(`/users/${this.form.id}`, payload);
        } else {
          response = this.allSitesMode
            ? await this.$api.post('/users', payload, { params: { all_sites: 1 } })
            : await this.$api.post('/users', payload);
        }

        this.$emit('saved', response.data);
      } catch (error) {
        if (error.response?.data?.errors) {
          const errors = error.response.data.errors;
          this.errorMessage = Object.values(errors).flat().join(' ');
        } else {
          this.errorMessage = 'Une erreur est survenue lors de l’enregistrement.';
        }
      } finally {
        this.isSubmitting = false;
      }
    },
  },
});
</script>

<style scoped>
.user-form-steps {
  display: grid;
  gap: 0.75rem;
  grid-template-columns: repeat(2, minmax(0, 1fr));
}

.user-form-step {
  align-items: center;
  background: var(--bs-light);
  border: 1px solid rgba(33, 37, 41, 0.12);
  border-radius: 8px;
  color: var(--bs-secondary-color);
  display: flex;
  font-weight: 700;
  gap: 0.65rem;
  padding: 0.75rem;
  text-align: left;
}

.user-form-step span {
  align-items: center;
  background: #fff;
  border-radius: 999px;
  display: inline-flex;
  height: 1.75rem;
  justify-content: center;
  width: 1.75rem;
}

.user-form-step.active {
  background: var(--bs-primary);
  border-color: var(--bs-primary);
  color: #fff;
}

.user-form-readonly {
  align-items: center;
  background-color: var(--bs-tertiary-bg);
  color: var(--bs-secondary-color);
  display: flex;
  min-height: 2.65rem;
}

.permission-grid {
  display: grid;
  gap: 0.75rem;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
  max-height: 52vh;
  overflow: auto;
  padding-right: 0.25rem;
}

.permission-module {
  border: 1px solid rgba(33, 37, 41, 0.1);
  border-radius: 8px;
  padding: 0.85rem;
}

.permission-actions {
  display: grid;
  gap: 0.55rem;
}

.permission-action {
  align-items: center;
  display: flex;
  gap: 0.5rem;
  margin: 0;
}
</style>
